import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Inter',sans-serif]">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center">
            <div className="mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 text-xs uppercase tracking-[0.2em] font-semibold">
              Legal
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6">
              Privacy Policy
            </h1>
            <p className="text-lg text-gray-600">
              Last updated: January 2025
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            
            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              FluxByte Ltd ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">2. Information We Collect</h2>
            
            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-6">2.1 Personal Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li>Fill out contact forms or request information</li>
              <li>Subscribe to our newsletter</li>
              <li>Register for an account or use our services</li>
              <li>Communicate with us via email or phone</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-6">
              This information may include: name, email address, phone number, company name, job title, and any other information you choose to provide.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-6">2.2 Automatically Collected Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you visit our website, we automatically collect certain information about your device, including:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li>IP address and browser type</li>
              <li>Operating system and device information</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website addresses</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">3. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li>Provide, operate, and maintain our services</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Send you newsletters, marketing communications, and updates (with your consent)</li>
              <li>Improve our website and services</li>
              <li>Analyze usage patterns and trends</li>
              <li>Detect, prevent, and address technical issues or fraudulent activity</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">4. Legal Basis for Processing (GDPR)</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you are from the European Economic Area (EEA), our legal basis for collecting and using your personal information depends on the data and context:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li><strong>Consent:</strong> You have given us permission to process your data for specific purposes</li>
              <li><strong>Contract:</strong> Processing is necessary to fulfill our contractual obligations</li>
              <li><strong>Legitimate Interests:</strong> Processing is in our legitimate business interests</li>
              <li><strong>Legal Obligation:</strong> Processing is required by law</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">5. Data Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell your personal information. We may share your information with:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li><strong>Service Providers:</strong> Third-party vendors who perform services on our behalf (e.g., email delivery, analytics, hosting)</li>
              <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>With Your Consent:</strong> When you have given us permission to share your information</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">6. Data Security</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">7. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">8. Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Restriction:</strong> Request restriction of processing your information</li>
              <li><strong>Portability:</strong> Request transfer of your information to another service</li>
              <li><strong>Objection:</strong> Object to our processing of your information</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent at any time (where processing is based on consent)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-6">
              To exercise these rights, please contact us at <a href="mailto:privacy@fluxbyte.ai" className="text-blue-600 hover:underline">privacy@fluxbyte.ai</a>
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">9. Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use cookies and similar tracking technologies to enhance your experience. You can control cookies through your browser settings. Types of cookies we use:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li><strong>Essential Cookies:</strong> Required for website functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
              <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">10. Third-Party Links</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">11. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Our services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">12. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable laws.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">13. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">14. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <p className="text-gray-700 mb-2"><strong>FluxByte Ltd</strong></p>
              <p className="text-gray-700 mb-2">Canal Road, Altrincham, WA14 1AE, United Kingdom</p>
              <p className="text-gray-700 mb-2">Email: <a href="mailto:privacy@fluxbyte.ai" className="text-blue-600 hover:underline">privacy@fluxbyte.ai</a></p>
              <p className="text-gray-700">Phone: <a href="tel:+441618837750" className="text-blue-600 hover:underline">0161 883 7750</a></p>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
